import { Camera } from 'iconsax-react'
import React, { useCallback, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import Webcam from 'react-webcam'

import UploadImage from '@/assets/icons/uploadImage.svg?react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { base64ToFile } from '@/utils/binary'
import { SocialMirrorPalette } from '@/utils/palette/SocialMirrorPalette'

interface ProfilePictureCaptureDialogProps {
  onImageChange: (file: File | undefined) => void
  trigger: React.ReactNode
  defaultTab?: 'camera' | 'upload'
}

export const ProfilePictureCaptureDialog = ({
  onImageChange,
  trigger,
  defaultTab = 'camera',
}: ProfilePictureCaptureDialogProps) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'camera' | 'upload'>(defaultTab)
  const [isCameraReady, setIsCameraReady] = useState(false)
  const webcamRef = useRef<Webcam>(null)

  // Camera capture
  const capturePhoto = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot()
    if (imageSrc) {
      const file = base64ToFile(imageSrc, `selfie-${Date.now()}.jpg`)
      onImageChange(file)
      setIsOpen(false)
    }
  }, [onImageChange])

  // File upload with dropzone
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0]
        onImageChange(file)
        setIsOpen(false)
      }
    },
    [onImageChange],
  )

  const onDropRejected = useCallback(() => {
    toast.error(t('feedback.toast.fileTooHeavy'))
  }, [t])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: {
      'image/png': ['.png', '.jpg', '.webp', '.jpeg'],
    },
    maxFiles: 1,
    maxSize: 10000000, // 10MB
  })

  // Handle camera user media
  const handleUserMedia = () => {
    setIsCameraReady(true)
  }

  // Reset camera state when dialog closes
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      setIsCameraReady(false)
      setActiveTab(defaultTab)
    }
  }

  return (
    <div className="flex flex-col gap-2 items-center w-full">
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>

        <DialogContent className="w-full max-w-xl p-2 border-none rounded-2xl bg-baseColorHighlight">
          <div className=" flex items-center justify-center h-12">
            <DialogTitle>
              {t('components.profilePictureCaptureDialog.title')}
            </DialogTitle>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={(value) =>
              setActiveTab(value as 'camera' | 'upload')
            }
            className="w-full"
          >
            <TabsList className="w-full bg-transparent justify-start border-b border-primaryColor30 p-0 rounded-none">
              <TabsTrigger
                value="camera"
                className="hover:cursor-pointer flex gap-2 w-full border-x-0 border-t-0 rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:text-primaryColor data-[state=active]:font-bold data-[state=active]:border-b-primaryColor data-[state=active]:shadow-none font-semibold text-neutralColor80 data-[state=inactive]:border-none max-sm:text-sm"
              >
                <Camera size={20} variant="Bold" />
                {t('components.profilePictureCaptureDialog.takePhoto')}
              </TabsTrigger>
              <TabsTrigger
                value="upload"
                className="hover:cursor-pointer flex gap-2 w-full border-x-0 border-t-0 rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:text-primaryColor data-[state=active]:font-bold data-[state=active]:border-b-primaryColor data-[state=active]:shadow-none font-semibold text-neutralColor80 data-[state=inactive]:border-none max-sm:text-sm"
              >
                <UploadImage width={20} height={20} />
                {t('components.profilePictureCaptureDialog.uploadImage')}
              </TabsTrigger>
            </TabsList>

            {/* Camera Tab */}
            <TabsContent value="camera" className="p-4 space-y-4">
              <div className="relative aspect-[4/3] bg-baseColor rounded-lg overflow-hidden">
                {/* Loading placeholder */}
                {!isCameraReady && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-baseColor">
                    <Camera
                      size={64}
                      variant="Bulk"
                      color={SocialMirrorPalette.neutralColor30}
                    />
                    <p className="text-neutralColor80 text-sm text-center px-4">
                      {t(
                        'components.profilePictureCaptureDialog.requestingCameraAccess',
                      )}
                    </p>
                  </div>
                )}

                <Webcam
                  ref={webcamRef}
                  audio={false}
                  screenshotFormat="image/jpeg"
                  videoConstraints={{
                    facingMode: 'user', // Front camera
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                  }}
                  onUserMedia={handleUserMedia}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex gap-2">
                <DialogClose asChild>
                  <Button variant="outline" className="flex-1">
                    {t('common.cancel')}
                  </Button>
                </DialogClose>
                <Button
                  onClick={capturePhoto}
                  className="flex-1"
                  variant="default"
                  disabled={!isCameraReady}
                >
                  {t('components.profilePictureCaptureDialog.capture')}
                </Button>
              </div>
            </TabsContent>

            {/* Upload Tab */}
            <TabsContent value="upload" className="p-4">
              <div
                {...getRootProps()}
                className={`
                                border-2 border-dashed rounded-lg p-8 cursor-pointer
                                transition-colors duration-200
                                ${
                                  isDragActive
                                    ? 'border-primaryColor bg-primaryColor bg-opacity-10'
                                    : 'border-neutralColor30 hover:border-primaryColor hover:bg-neutralColor5 hover:bg-opacity-5'
                                }
                            `}
              >
                <input {...getInputProps()} />
                <div className="flex flex-col items-center gap-3">
                  <UploadImage
                    width={48}
                    height={48}
                    color={
                      isDragActive
                        ? SocialMirrorPalette.primary
                        : SocialMirrorPalette.neutralColor60
                    }
                  />
                  <div className="text-center">
                    <p className="text-neutralColor font-medium mb-1">
                      {isDragActive
                        ? t('components.profilePictureCaptureDialog.dropHere')
                        : t(
                            'components.profilePictureCaptureDialog.clickOrDrag',
                          )}
                    </p>
                    <p className="text-neutralColor80 text-sm">
                      {t(
                        'components.profilePictureCaptureDialog.supportedFormats',
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <DialogClose asChild>
                  <Button variant="outline" className="w-full">
                    {t('common.cancel')}
                  </Button>
                </DialogClose>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  )
}
